import React, { useEffect, useState } from 'react'
import {
    PieChart,
    Pie,
    Cell,
    ResponsiveContainer,
} from "recharts";
import { toWords } from "number-to-words";
import EmiSchedule from './EmiSchedule';
import './calculator.css';

const Calculator = ({
    minAmount = 500000,
    maxAmount = 20000000,
    minTenure = 5,
    maxTenure = 30,
    minRate = 6,
    maxRate = 20,
    tenureUnit = "years",
    initialAmount = 1000000,
    initialRate = 6.5,
    initialTenure = 20
}) => {
    const [loanAmount, setLoanAmount] = useState(initialAmount);
    const [tenure, setTenure] = useState(initialTenure);
    const [rate, setRate] = useState(initialRate);
    // Set current date as default
    const [previousLoanDate, setPreviousLoanDate] = useState(new Date().toISOString().split('T')[0]);

    const [emi, setEmi] = useState(0);
    const [totalInterest, setTotalInterest] = useState(0);
    const [totalPayable, setTotalPayable] = useState(0);
    const [isOpen, setIsOpen] = useState(false);
    const [remainingMonths, setRemainingMonths] = useState(0);
    const [elapsedMonths, setElapsedMonths] = useState(0);
    const dateInputRef = React.useRef(null);

    useEffect(() => {
        calculateEMIHome();
    }, [loanAmount, tenure, rate, previousLoanDate]);

    // Function to open date picker
    const openDatePicker = () => {
        if (dateInputRef.current) {
            dateInputRef.current.showPicker();
        }
    };

    const calculateEMIHome = () => {
        const P = loanAmount;
        const r = rate / 12 / 100;
        const n = tenure * 12;

        // Calculate elapsed months from loan start date
        const loanDate = new Date(previousLoanDate);
        const today = new Date();
        
        // Calculate months difference
        const yearsDiff = today.getFullYear() - loanDate.getFullYear();
        const monthsDiff = today.getMonth() - loanDate.getMonth();
        const totalElapsedMonths = (yearsDiff * 12) + monthsDiff;
        
        // Ensure elapsed months is not negative or more than tenure
        const validElapsedMonths = Math.max(0, Math.min(totalElapsedMonths, n));
        setElapsedMonths(validElapsedMonths);
        
        // Calculate remaining months
        const remaining = Math.max(0, n - validElapsedMonths);
        setRemainingMonths(remaining);

        // Standard EMI calculation
        const emiVal = (P * r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1);
        const total = emiVal * n;
        const interest = total - P;

        setEmi(emiVal || 0);
        setTotalInterest(interest || 0);
        setTotalPayable(total || 0);
    };

    const COLORS = ["#111827", "#1a237e"];
    const paymentData = [
        { name: "Principal", value: loanAmount },
        { name: "Interest", value: totalInterest },
    ];

    const NumberToText = (amount) => {
        if (!amount || amount <= 0) return <p>N/A</p>;
        return (
            <p>
                {toWords(Math.round(amount))
                    .replace(/\b\w/g, c => c.toUpperCase())}
            </p>
        );
    };

    // Helper to format labels (Cr, Lac, K)
    const formatLabel = (val) => {
        if (val >= 10000000) return `${val / 10000000} Cr`;
        if (val >= 100000) return `${val / 100000} Lac`;
        if (val >= 1000) return `${val / 1000} K`;
        return val;
    };

    const toggleSchedule = () => {
        setIsOpen(!isOpen);
    };

    return (
        <div className="homeloan-emi-calculator">
            <div className="calculator-card">
                <div>
                    {/* Loan Date Field */}
                    <div className="date-field-wrapper" onClick={openDatePicker}>
                        <label>Loan Start Date</label>
                        <input
                            ref={dateInputRef}
                            type="date"
                            className="date-input"
                            value={previousLoanDate}
                            onChange={(e) => setPreviousLoanDate(e.target.value)}
                            placeholder="Select loan start date"
                        />
                    </div>

                    <label>Loan Amount <span>₹{loanAmount.toLocaleString()}</span></label>
                    <input
                        type="range"
                        min={minAmount}
                        max={maxAmount}
                        /* FIX: If the amount is small, use 1000 step. 
                           If large, use 10000. This prevents the "960000" snapping issue.
                        */
                        step={loanAmount < 1000000 ? 1000 : 10000}
                        value={loanAmount}
                        onChange={(e) => setLoanAmount(+e.target.value)}
                    />
                    <div className='text-under-range'>
                        <p>{formatLabel(minAmount)}</p>
                        <p>{formatLabel(maxAmount)}</p>
                    </div>

                    <label>Loan Tenure <span>{tenure} {tenureUnit}</span></label>
                    <input
                        type="range"
                        min={minTenure}
                        max={maxTenure}
                        step="1"
                        value={tenure}
                        onChange={(e) => setTenure(+e.target.value)}
                    />
                    <div className='text-under-range'>
                        <p>{minTenure}</p>
                        <p>{maxTenure}</p>
                    </div>

                    <label>Rate of Interest <span>{rate}%</span></label>
                    <input
                        type="range"
                        min={minRate}
                        max={maxRate}
                        step="0.1"
                        value={rate}
                        onChange={(e) => setRate(+e.target.value)}
                    />
                    <div className='text-under-range'>
                        <p>{minRate}</p>
                        <p>{maxRate}</p>
                    </div>
                </div>

                <div className="emi-box">
                    <p>Monthly Loan EMI</p>
                    <h2>₹{emi.toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g, ",")}</h2>
                    <div className="emi-to-words">{NumberToText(emi.toFixed(0))}</div>
                </div>

                <div className="breakdown-box">
                    <h4>Payment Breakdown</h4>
                    <div className="chart-wrapper">
                        <ResponsiveContainer width={220} height={180}>
                            <PieChart>
                                <Pie
                                    data={paymentData}
                                    dataKey="value"
                                    innerRadius={40}
                                    outerRadius={80}
                                    paddingAngle={2}
                                >
                                    {paymentData.map((_, index) => (
                                        <Cell key={index} fill={COLORS[index]} />
                                    ))}
                                </Pie>
                            </PieChart>
                        </ResponsiveContainer>
                    </div>

                    <p>
                        <span><span className="legend principal"></span> Principal Amount</span>
                        <span>₹{loanAmount.toLocaleString()}</span>
                    </p>
                    <p>
                        <span><span className="legend interest"></span> Interest Payable</span>
                        <span>₹{totalInterest.toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g, ",")}</span>
                    </p>
                    <p className="total-payable">
                        <span>Total Amount Payable:</span>
                        <span>₹{totalPayable.toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g, ",")}</span>
                    </p>
                    
                    {/* Date-based information */}
                    <div className="date-based-info">
                        <p>
                            <span>📅 Elapsed Months:</span>
                            <span>{elapsedMonths} months</span>
                        </p>
                        <p>
                            <span>⏳ Remaining Months:</span>
                            <span>{remainingMonths} months</span>
                        </p>
                        <p>
                            <span>💰 Total EMIs Paid:</span>
                            <span>₹{(emi * elapsedMonths).toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g, ",")}</span>
                        </p>
                        <p>
                            <span>💵 Remaining Amount:</span>
                            <span>₹{(emi * remainingMonths).toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g, ",")}</span>
                        </p>
                    </div>
                    
                    <button onClick={toggleSchedule} className="toggle-button">
                        {isOpen ? "Hide EMI Schedule" : "Show EMI Schedule"}
                    </button>
                </div>
            </div>

            <div className="emi-wrapper">
                <div
                    className={`emi-content ${isOpen ? "open" : "collapsed"}`}
                    style={{
                        maxHeight: isOpen ? "2000px" : "0px",
                        overflow: "hidden",
                        transition: "max-height 0.5s ease",
                    }}
                >
                    <EmiSchedule loanAmount={loanAmount} rate={rate} tenure={tenure} />
                </div>
            </div>
        </div>
    );
}

export default Calculator;